import { useState, useRef } from "react";
import { Button }   from "@/components/ui/button";
import { Input }    from "@/components/ui/input";
import { Plus, Send, X } from "lucide-react";
import { ChatService } from "@/services/chat.service";
import { useParams } from "react-router";
import {toast} from "sonner";
import {realtime} from "@/lib/realtime.ts";
import { holdMessageOr } from "@/lib/appointment-field-actions";

const MAX_MB = 10;
const accept  = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt";

export default function MessageInput() {
    const { roomId } = useParams();
    const [text, setText]   = useState("");
    const [files, setFiles] = useState<File[]>([]);
    const [sending, setSending] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const typingRef = useRef<NodeJS.Timeout>(null);

    const handleTyping = (val: string) => {
        setText(val);
        if (roomId) realtime.broadcastTyping(roomId, true);

        if(typingRef.current != null)
            clearTimeout(typingRef.current);

        typingRef.current = setTimeout(() => {
            if (roomId) realtime.broadcastTyping(roomId, false);
        }, 1500);
    };

    const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const arr = Array.from(e.target.files);
        const valid = arr.filter(f => f.size <= MAX_MB * 1024 * 1024);
        if (valid.length < arr.length) toast.error("Some files > 10 MB were skipped");
        setFiles(prev => [...prev, ...valid].slice(0, 5));
        e.target.value = "";
    };

    const onSend = async () => {
        if (!text.trim() && !files.length) return;
        if (!roomId) {
            toast.error("Open a chat before sending a message.");
            return;
        }

        const message = text;
        const selectedFiles = files;
        setSending(true);
        realtime.broadcastTyping(roomId, false);

        try {
            const result = await ChatService.sendMessage(roomId, message, selectedFiles);
            setText(result.failedText ?? "");
            setFiles(result.failedFiles);

            if (result.failedText || result.failedFiles.length > 0) {
                toast.error(holdMessageOr(result.failure, "Some message content could not be sent. Please try again."));
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message ?? "Failed to send message.");
        } finally {
            setSending(false);
        }
    };

    const removeFile = (idx: number) =>
        setFiles(arr => arr.filter((_, i) => i !== idx));

    return (
        <div className="p-4 border-t">
            {/* previews */}
            {files.length > 0 && (
                <div className="flex space-x-2 mb-2 overflow-x-auto">
                    {files.map((f, i) =>
                        f.type.startsWith("image") ? (
                            <div key={i} className="relative">
                                <img src={URL.createObjectURL(f)} className="h-16 w-16 object-cover rounded" />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="absolute -top-2 -right-2"
                                    onClick={() => removeFile(i)}
                                >
                                    <X className="h-3 w-3" />
                                </Button>
                            </div>
                        ) : (
                            <div key={i} className="relative px-2 py-1 bg-muted rounded text-xs">
                                {f.name}
                                <Button variant="ghost" size="icon" className="absolute -top-2 -right-2" onClick={() => removeFile(i)}>
                                    <X className="h-3 w-3" />
                                </Button>
                            </div>
                        )
                    )}
                </div>
            )}

            <div className="flex space-x-2">
                <input
                    type="file"
                    accept={accept}
                    multiple
                    ref={fileRef}
                    className="hidden"
                    onChange={onPick}
                />
                <Button size="icon" onClick={() => fileRef.current?.click()} disabled={sending}>
                    <Plus className="h-4 w-4" />
                </Button>

                <Input
                    value={text}
                    onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey && !sending) {
                            e.preventDefault();
                            onSend();
                        }
                    }}
                    placeholder="Type a message…"
                    className="flex-1"
                    onChange={(e) => handleTyping(e.target.value)}
                    ref={inputRef}
                />

                <Button size="icon" onClick={onSend} disabled={sending}>
                    <Send className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
}
